import React from 'react';
import type { Site, Page, Component, PageLayout } from '../schemas/validation';
import { WebsiteLayout } from './WebsiteLayout';
import { ComponentList } from './ComponentList';
import { ComponentRenderer } from './ComponentRenderer';
import { PageContextProvider } from '../contexts/PageContext';

interface RenderedWebsiteProps {
  website: Site;
  page: Page;
  components: Component[];
  navigation: Array<{ title: string; slug: string; orderPosition: number }>;
  theme: unknown;
  layout: PageLayout | null;
  showBranding: boolean;
  /** Identifier used in the public website context (subdomain or custom domain). */
  identifier: string;
}

export const RenderedWebsite: React.FC<RenderedWebsiteProps> = ({
  website,
  page,
  components,
  navigation,
  theme,
  layout,
  showBranding,
  identifier,
}) => {
  const layoutComponents = layout?.components || [];
  const sortedLayoutComponents = Array.isArray(layoutComponents)
    ? [...layoutComponents].sort(
        (a: any, b: any) => (a.order || a.orderPosition || 0) - (b.order || b.orderPosition || 0)
      )
    : [];
  const contentAreaIndex = sortedLayoutComponents.findIndex((c: any) => c.type === 'content-area');
  const headerLayoutComponents =
    contentAreaIndex >= 0 ? sortedLayoutComponents.slice(0, contentAreaIndex) : [];
  const footerLayoutComponents =
    contentAreaIndex >= 0 ? sortedLayoutComponents.slice(contentAreaIndex + 1) : [];

  return (
    <PageContextProvider pageId={page.id} siteId={website?.id ?? null} pageSlug={page.slug ?? ''}>
      <WebsiteLayout
        website={website}
        page={page}
        navigation={navigation}
        theme={theme}
        showBranding={showBranding}
      >
        <div id="top" />

        {headerLayoutComponents.length > 0 && (
          <div className="layout-header-components">
            {headerLayoutComponents
              .filter((c: any) => c.visible !== false)
              .map((component: any) => (
                <ComponentRenderer key={component.id} component={component} isEditing={false} />
              ))}
          </div>
        )}

        {components.length > 0 ? (
          <ComponentList
            components={components}
            websiteContext={{
              subdomain: identifier,
              website,
              isPublicView: true,
            }}
          />
        ) : (
          <div className="min-h-96 flex items-center justify-center">
            <div className="text-center max-w-md mx-auto px-4">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{page.title}</h1>
              <p className="text-gray-600">This page is empty. Add some content to get started!</p>
            </div>
          </div>
        )}

        {footerLayoutComponents.length > 0 && (
          <div className="layout-footer-components">
            {footerLayoutComponents
              .filter((c: any) => c.visible !== false)
              .map((component: any) => (
                <ComponentRenderer key={component.id} component={component} isEditing={false} />
              ))}
          </div>
        )}
      </WebsiteLayout>
    </PageContextProvider>
  );
};
